/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { StatusStyle } from '@bcl/design-system';
export type DateOnly = Readonly<Date>;
export type TimeOnly = Readonly<string>;
export type WithRequired<T, K extends keyof T> = T & Required<Pick<T, K>>;
export type ActionPoint = Omit<Action, 'status' | 'responsibility'> & {
  icon: {
    name: string;
    color: string;
  };
  status: {
    value: string;
    label: string;
    style?: StatusStyle;
  };
  responsibility?: {
    value: string;
    label: string;
  };
};

export type InputTimeValue = string | null | undefined;

export type ActionContext = unknown;

export type AddActionContext = Readonly<{
  userId: string;
}>;

export type CompanyClicked = {
  company: ActionPoint['company'];
  context: ActionContext;
};

export type Action = {
  id: number;
  number?: string;
  context: ActionContext;
  actionType: string;
  actionTypeId: number;
  title: string;
  customerId: number;
  customerName?: string;
  locationId: number;
  locationName?: string;
  areaId: number;
  areaName?: string;
  company?: {
    id: string;
    name: string;
  };
  description?: string;
  deadline?: DateOnly;
  deadlineTime?: TimeOnly;
  duration?: TimeOnly;
  status: string;
  statusId: number;
  assignee?: { id: string; name: string; image?: string };
  assigneeId: string | null;
  created: {
    at: Date;
    by: string;
  };
  updated: {
    at: Date;
    by: string;
  };
  responsibility?: string;
  responsibilityId: number;
  parentObject: string;
  parentId: number;
  eventId?: string;
  photos?: Photo[];
  contactId?: string;
  finalApprovalBy?: string;
  finalApprovalDate?: string;
  initialApprovalBy?: string;
  reason?: string;
  priorityId?: string;
  isCommentAdded?: boolean;
};

export type ActionEdit = {
  id?: number;
  type: string;
  assigneeId: string | undefined;
  deadline: DateOnly | undefined;
  deadlineTime: TimeOnly | undefined;
  duration: TimeOnly | undefined;
  status: string;
  responsibility: string | undefined;
  title: string;
  description: string | undefined;
  photos?: Photo[];
  contactId: string | undefined;
  initialApprovalBy: string | undefined;
  initialApprovalDate: string | undefined;
  finalApprovalBy: string | undefined;
  finalApprovalDate: string | undefined;
  reason: string | undefined;
  priorityId: string | undefined;
};

export type ActionsList = {
  actions: Action[];
  totalEntries: number;
  futureToDoCount?: number;
  lateToDoCount?: number;
  todayToDoCount?: number;
};
export type Photo = {
  id: number;
  fileName: string;
  referenceId: string;
  size: number;
  description: string;
  mimeType: string;
  edited?: boolean;
  type: string;
  blobUrl?: string;
};
export type DateRange = '0' | '1' | '2' | '3' | '4';
export type ActionsListFilter = {
  date: DateRange | null;
  status: string | null;
};

export type ActionType = {
  value: string;
  icon: {
    name: string;
    color: string;
  };
  defaultTitle: string;
  sendToOutlookCalendar: boolean;
  actionable: boolean;
  text1?: string;
};

export type ActionTypes = Array<ActionType>;

export type ActionStatus = {
  value: string;
  label: string;
  style: StatusStyle;
};

export type ActionStatuses = Array<ActionStatus>;

export type ActionPriority = {
  value: string;
  label: string;
  color: string;
};

export type ActionPriorities = Array<ActionPriority>;

export type ActionResponsibility = {
  value: string;
  label: string;
};

export type ActionResponsibilities = Array<ActionResponsibility>;

export function mapActionIcon(
  actionTypeId: number,
  actionTypes: ActionTypes
): ActionPoint['icon'] {
  return (
    actionTypes.find((x) => x.value === actionTypeId.toString())?.icon || {
      name: 'phone-outgoing-icon',
      color: '#7f7f7f',
    }
  );
}

export const MediumPriorityLabel = 'Medium';

export function mapAction(
  action: Action,
  actionTypes: ActionTypes,
  actionPriorities: ActionPriorities,
  actionStatuses: ActionStatuses,
  actionResponsibilities: ActionResponsibilities
): ActionPoint | undefined {
  if (!actionTypes.find((x) => x.value === action.actionTypeId.toString())) {
    return;
  }

  const actionStatus = actionStatuses.find(
    (x) => x.value === action.statusId.toString()
  );

  return {
    ...action,
    icon: mapActionIcon(action.actionTypeId, actionTypes),
    title:
      action.title ||
      actionTypes.find((x) => x.value === action.actionTypeId.toString())
        ?.defaultTitle ||
      '',
    status: {
      value: action.statusId.toString(),
      label: actionStatus?.label || '',
      style: actionStatus?.style,
    },
    responsibility:
      action.responsibilityId !== null && action.responsibilityId !== undefined
        ? {
            value: action.responsibilityId.toString(),
            label:
              actionResponsibilities.find(
                (x) => x.value === action.responsibilityId.toString()
              )?.label || '',
          }
        : undefined,
  };
}

export function toIso8601String(
  date: string | Date | undefined
): string | undefined {
  return date ? new Date(date).toISOString().slice(0, 10) : undefined;
}

export function toTimeOnly(
  inputTimeValue: InputTimeValue
): TimeOnly | undefined {
  if (!inputTimeValue) {
    return undefined;
  }

  return `${inputTimeValue}:00`;
}

export function toInputTimeValue(
  time: TimeOnly | null | undefined
): InputTimeValue {
  if (!time) {
    return time;
  }

  return time.split(':').slice(0, 2).join(':');
}

export const HoursQuarters: Array<InputTimeValue> = Array.from(
  { length: 24 * 4 },
  (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }
);
export type AddedAction = {
  action: Omit<ActionEdit, 'id'>;
  photosAdded?: Photo[];
  photosDeleted?: Photo[];
};
export type EditedAction = {
  action: ActionEdit;
  id: number;
  photosAdded?: Photo[];
  photosDeleted?: Photo[];
  photosUpdated?: Photo[];
};
export type DeletedAction = { id: number; photos?: Photo[] };
type AddActionPoint = {
  type: 'add';
  action: AddedAction;
};

type EditActionPoint = {
  type: 'edit';
  action: EditedAction;
};

type DeleteActionPoint = {
  type: 'delete';
  action: DeletedAction;
};

export type ActionPointChange =
  | AddActionPoint
  | EditActionPoint
  | DeleteActionPoint;
